/* ============================================================
   Telltale — marketing motion layer

   Six jobs, all progressive enhancement. With JS off (or if this throws)
   every block still renders correctly — the CSS resting state IS the
   finished state, and .is-inview only replays it as animation.

     1. .is-inview when a block scrolls into view (drives CSS animations)
     2. count-up for [data-tt-count] numbers
     2b. re-arming that count-up inside looping scenes ([data-tt-recount])
     3. the audience segment switcher  ([data-tt-live])
     4. the campaign studio            ([data-tt-studio])
     5. the pricing tier selector      ([data-tt-pricing])

   The three switchers use a short simulated fetch so the skeleton state is
   actually perceptible — an instant swap reads as nothing happening.

   Honours prefers-reduced-motion: numbers snap to final values, panels
   swap immediately, nothing animates.
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var SWAP_MS = 380;

  /* ---------- 2. count-up (declared first: reveal calls it) ---------- */
  function formatNum(v, el) {
    var dp = parseInt(el.getAttribute('data-tt-decimals') || '0', 10);
    var out = dp > 0 ? v.toFixed(dp) : String(Math.round(v));
    if (el.getAttribute('data-tt-commas') === 'true') {
      var parts = out.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      out = parts.join('.');
    }
    return (el.getAttribute('data-tt-prefix') || '') + out + (el.getAttribute('data-tt-suffix') || '');
  }

  function countUp(el) {
    if (el.dataset.ttDone === '1') return;
    el.dataset.ttDone = '1';
    var target = parseFloat(el.getAttribute('data-tt-count'));
    if (isNaN(target)) return;
    if (reduce) { el.textContent = formatNum(target, el); return; }

    var dur = parseInt(el.getAttribute('data-tt-duration') || '1500', 10);
    var start = null;
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      // easeOutCubic — fast then settles, reads as "resolving"
      el.textContent = formatNum(target * (1 - Math.pow(1 - p, 3)), el);
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = formatNum(target, el);
    }
    requestAnimationFrame(step);
  }

  /* ---------- 1. reveal on scroll ---------- */
  function markInView(el) {
    el.classList.add('is-inview');
    el.querySelectorAll('[data-tt-count]').forEach(countUp);
  }

  var targets = document.querySelectorAll('[data-tt-reveal]');
  if (!('IntersectionObserver' in window)) {
    targets.forEach(markInView);
  } else {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        markInView(e.target);
        io.unobserve(e.target);
      });
    }, { threshold: 0.22, rootMargin: '0px 0px -8% 0px' });
    targets.forEach(function (t) { io.observe(t); });
  }

  /* ---------- 2b. re-arm the count-up inside a looping scene ----------
     A [data-tt-count] fires once by design. Inside the homepage scene player
     the same scene returns every cycle, so the number would only ever animate
     on the first pass — and usually while that scene was still off stage.

     Re-arming on the SCENE'S OWN CSS animation events keeps the two in step
     without introducing a second clock: `animationstart` catches the first
     appearance and `animationiteration` catches every loop after it, both
     emitted by the same keyframes that move the scene. A setInterval here
     would drift out of phase the moment the tab was throttled. */
  document.querySelectorAll('[data-tt-recount]').forEach(function (el) {
    var scene = el.closest('.tt-scene');
    if (!scene) return;
    function rearm(e) {
      if (e.animationName !== 'ttflow-scene') return;
      el.dataset.ttDone = '';
      countUp(el);
    }
    scene.addEventListener('animationstart', rearm);
    scene.addEventListener('animationiteration', rearm);
  });

  /* ---------- shared: brief loading state, then swap ---------- */
  function withLoading(root, apply) {
    if (reduce) { apply(); return; }
    root.setAttribute('data-loading', 'true');
    window.setTimeout(function () {
      apply();
      root.setAttribute('data-loading', 'false');
    }, SWAP_MS);
  }

  function selectChip(chips, chip) {
    chips.forEach(function (c) { c.setAttribute('aria-selected', String(c === chip)); });
  }

  /* ---------- shared: chips reveal one of a set of panels ----------
     Jobs 4 and 5 are the same interaction over different markup, so they
     share this. `attr` is the data attribute that pairs a chip with its
     panel (data-camp for the studio, data-tier for pricing). */
  function wireChipPanels(root, chipSel, panelSel, attr) {
    var chips = Array.prototype.slice.call(root.querySelectorAll(chipSel));
    var panels = Array.prototype.slice.call(root.querySelectorAll(panelSel));
    if (!chips.length || !panels.length) return;

    function show(key) {
      panels.forEach(function (p) {
        if (p.getAttribute(attr) === key) {
          p.removeAttribute('hidden');
          p.querySelectorAll('[data-tt-count]').forEach(countUp);
        } else {
          p.setAttribute('hidden', '');
        }
      });
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        selectChip(chips, chip);
        withLoading(root, function () { show(chip.getAttribute(attr)); });
      });
    });
  }

  /* ---------- 3. audience segment switcher ---------- */
  document.querySelectorAll('[data-tt-live]').forEach(function (root) {
    var dataEl = root.querySelector('[data-tt-live-data]');
    var content = root.querySelector('.tt-live__content');
    var chips = Array.prototype.slice.call(root.querySelectorAll('.tt-segchip'));
    if (!dataEl || !content || !chips.length) return;

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (err) { return; }

    function render(key) {
      var seg = data[key];
      if (!seg) return;
      var html = '<div class="tt-live__metrics">';
      seg.metrics.forEach(function (m) {
        html += '<div><div class="tt-live__num" data-tt-count="' + m.value + '"' +
                (m.prefix ? ' data-tt-prefix="' + m.prefix + '"' : '') +
                (m.suffix ? ' data-tt-suffix="' + m.suffix + '"' : '') +
                (m.commas ? ' data-tt-commas="true"' : '') +
                (m.decimals ? ' data-tt-decimals="' + m.decimals + '"' : '') +
                '>0</div><div class="tt-live__lbl">' + m.label + '</div></div>';
      });
      html += '</div>';
      seg.rows.forEach(function (r) {
        html += '<div class="tt-live__row"><span class="tt-live__row-name">' + r.name +
                '</span><span class="tt-live__row-meta">' + r.meta +
                '</span><span class="tt-live__row-score">' + r.score + '</span></div>';
      });
      html += '<p class="tt-live__foot">' + seg.foot + '</p>';
      content.innerHTML = html;
      content.querySelectorAll('[data-tt-count]').forEach(countUp);
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        selectChip(chips, chip);
        withLoading(root, function () { render(chip.getAttribute('data-seg')); });
      });
    });

    chips[0].setAttribute('aria-selected', 'true');
    render(chips[0].getAttribute('data-seg')); // initial paint, no loading flash
  });

  /* ---------- 4. campaign studio ---------- */
  document.querySelectorAll('[data-tt-studio]').forEach(function (root) {
    wireChipPanels(root, '.tt-segchip', '.tt-studio__panel', 'data-camp');
  });

  /* ---------- 5. pricing tier selector ---------- */
  document.querySelectorAll('[data-tt-pricing]').forEach(function (root) {
    wireChipPanels(root, '.tt-tierchip', '.tt-tierpanel', 'data-tier');
  });
})();
